/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { Form, Input, Select, DatePicker } from 'formik-antd';
import { Modal, message, Spin, Col } from 'antd';
import locale from 'antd/es/date-picker/locale/pt_BR';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import errorHandler from '~/Utils/errorHandler';
import InputCurrency from '~/components/Form/InputCurrency';
import Row from '~/components/Row';
import FormControl from '~/components/Form/FormControl';
import { ModalFooter } from '~/components/Modal';
import { decrypt, dynamicCost, getSuccessMessageByModule } from '~/Utils/index';

import api from '~/services/api';

const initialValues = {
  lot_number: '',
  initial_date: '',
  final_date: '',
  congress_id: '',
  graduation_student_value: 0,
  graduation_student_associated_value: 0,
  professional_value: 0,
  professional_associated_value: 0,
  pos_graduation_student_value: 0,
  pos_graduation_student_associated_value: 0,
  postdoctoral_student_associated_value: 0,
  postdoctoral_student_value: 0,
};

export default function PaymentLotForm({ visible, onClose, paymentLotID }) {
  const { t } = useTranslation();
  const [recordData, setRecordData] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [congressOptions, setCongressOptions] = useState([]);
  const { permissions } =
    JSON.parse(decrypt(localStorage.getItem('@App:user'))) !== null &&
    JSON.parse(decrypt(localStorage.getItem('@App:user')));

  const fetchCongresses = async () => {
    try {
      const { data } = await api.get('/congresses');
      setCongressOptions(data);
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleSave = async (values, { setErrors }) => {
    setLoading(true);
    delete values.isEdit;
    try {
      if (values.id) {
        await api.put(`/paymentLots/${values.id}`, values);
        message.success(getSuccessMessageByModule(t('screens:paymentLots.titleSingular'), t('messages:successUpdated')));
      } else {
        await api.post('/paymentLots', values);
        message.success(getSuccessMessageByModule(t('screens:paymentLots.titleSingular'), t('messages:successCreated')));
      }
      onClose();
    } catch (error) {
      setErrors(errorHandler(error));
    }
    setLoading(false);
  };

  const fetchRecord = async () => {
    if (!paymentLotID) {
      setRecordData(initialValues);
    } else {
      const response = await api.get(`/paymentLots/${paymentLotID}`);
      const data = JSON.parse(response.data);
      setRecordData(data);
    }
  };

  const fetchScreenData = async () => {
    setLoading(true);
    try {
      await fetchCongresses();
      await fetchRecord();
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      fetchScreenData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const PaymentLotSchema = Yup.object().shape({
    lot_number: Yup.number().integer().min(1).max(3).required(),
    initial_date: Yup.date().required(),
    final_date: Yup.date().required(),
    congress_id: Yup.number().integer().required(),
    graduation_student_value: Yup.number().min(.01).required(),
    graduation_student_associated_value: Yup.number().min(0).required(),
    professional_value: Yup.number().min(.01).required(),
    professional_associated_value: Yup.number().min(0).required(),
    pos_graduation_student_value: Yup.number().min(.01).required(),
    pos_graduation_student_associated_value: Yup.number().min(0).required(),
    postdoctoral_student_associated_value: Yup.number().min(0).required(),
    postdoctoral_student_value: Yup.number().min(.01).required(),
  });

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={recordData}
      enableReinitialize
      onSubmit={handleSave}
      validationSchema={PaymentLotSchema}
    >
      {({ errors, isSubmitting, submitForm, values, resetForm, setValues }) => (
        <Modal
          title={
            recordData.id
              ? `${t('screens:editing')}: Lote Nº ${
                  recordData.lot_number
                } - ${recordData.congress.description.toUpperCase()}`
              : t('screens:paymentLots.title')
          }
          onCancel={onClose}
          style={{ maxWidth: '950px' }}
          afterClose={resetForm}
          visible={visible}
          loading={loading || isSubmitting}
          centered
          width="90%"
          footer={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <ModalFooter
              type={permissions && !permissions.includes('@paymentLot/edit') && 'info'}
              onOk={submitForm}
              loading={loading || isSubmitting}
              onCancel={onClose}
            />
          }
        >
          <Spin spinning={loading || isSubmitting}>
            <Form>
              <Row>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Row>
                    <FormControl
                      cols={{ xs: 12 }}
                      error={errors.congress_id}
                      field="congress_id"
                      label={t('fields:congress_id')}
                      required
                    >
                      <Select
                        name="congress_id"
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                        allowClear
                        optionFilterProp="children"
                      >
                        {congressOptions &&
                          congressOptions.map((item) => (
                            <Select.Option key={item.id} value={item.id}>
                              {item.description.toUpperCase()} - {item.place}
                            </Select.Option>
                          ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      cols={{ xs: 12 }}
                      error={errors.lot_number}
                      field="lot_number"
                      label={t('screens:paymentLots.data.lot_number')}
                      required
                    >
                      <Input
                        name="lot_number"
                        maxLength="1"
                        disabled={recordData.id || (permissions && !permissions.includes('@paymentLot/edit'))}
                      />
                    </FormControl>
                    <FormControl
                      error={errors.initial_date}
                      cols={{ xs: 12 }}
                      field="initial_date"
                      required
                      label={t('fields:initial_date')}
                    >
                      <DatePicker
                        format="DD/MM/Y"
                        name="initial_date"
                        placeholder={t('messages:select')}
                        locale={locale}
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                      />
                    </FormControl>
                    <FormControl
                      error={errors.final_date}
                      cols={{ xs: 12 }}
                      field="final_date"
                      required
                      label={t('fields:final_date')}
                    >
                      <DatePicker
                        format="DD/MM/Y"
                        name="final_date"
                        placeholder={t('messages:select')}
                        locale={locale}
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                      />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 6 }}
                      error={errors.graduation_student_value}
                      field="graduation_student_value"
                      label={t('fields:graduation_student_value')}
                      required
                    >
                      <InputCurrency
                        name="graduation_student_value"
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                        currency="R$"
                        number={false}
                        maxLength="9"
                        onChange={(event) => {
                          setTimeout(() => {
                            setValues({ ...values, graduation_student_value: dynamicCost(event.target.value) });
                          }, 100);
                        }}
                      />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 6 }}
                      error={errors.graduation_student_associated_value}
                      field="graduation_student_associated_value"
                      label={t('fields:graduation_student_associated_value')}
                      required
                    >
                      <InputCurrency
                        name="graduation_student_associated_value"
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                        currency="R$"
                        number={false}
                        maxLength="9"
                        onChange={(event) => {
                          setTimeout(() => {
                            setValues({
                              ...values,
                              graduation_student_associated_value: dynamicCost(event.target.value),
                            });
                          }, 100);
                        }}
                      />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 6 }}
                      error={errors.postdoctoral_student_value}
                      field="postdoctoral_student_value"
                      label={t('fields:postdoctoral_student_value')}
                      required
                    >
                      <InputCurrency
                        name="postdoctoral_student_value"
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                        currency="R$"
                        number={false}
                        maxLength="9"
                        onChange={(event) => {
                          setTimeout(() => {
                            setValues({ ...values, postdoctoral_student_value: dynamicCost(event.target.value) });
                          }, 100);
                        }}
                      />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 6 }}
                      error={errors.postdoctoral_student_associated_value}
                      field="postdoctoral_student_associated_value"
                      label={t('fields:postdoctoral_student_associated_value')}
                      required
                    >
                      <InputCurrency
                        name="postdoctoral_student_associated_value"
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                        currency="R$"
                        number={false}
                        maxLength="9"
                        onChange={(event) => {
                          setTimeout(() => {
                            setValues({
                              ...values,
                              postdoctoral_student_associated_value: dynamicCost(event.target.value),
                            });
                          }, 100);
                        }}
                      />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 6 }}
                      error={errors.professional_value}
                      field="professional_value"
                      label={t('fields:professional_value')}
                      required
                    >
                      <InputCurrency
                        name="professional_value"
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                        currency="R$"
                        number={false}
                        maxLength="9"
                        onChange={(event) => {
                          setTimeout(() => {
                            setValues({ ...values, professional_value: dynamicCost(event.target.value) });
                          }, 100);
                        }}
                      />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 6 }}
                      error={errors.professional_associated_value}
                      field="professional_associated_value"
                      label={t('fields:professional_associated_value')}
                      required
                    >
                      <InputCurrency
                        name="professional_associated_value"
                        currency="R$"
                        number={false}
                        maxLength="9"
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                        onChange={(event) => {
                          setTimeout(() => {
                            setValues({ ...values, professional_associated_value: dynamicCost(event.target.value) });
                          }, 100);
                        }}
                      />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 6 }}
                      error={errors.pos_graduation_student_value}
                      field="pos_graduation_student_value"
                      label={t('fields:pos_graduation_student_value')}
                      required
                    >
                      <InputCurrency
                        name="pos_graduation_student_value"
                        currency="R$"
                        number={false}
                        maxLength="9"
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                        onChange={(event) => {
                          setTimeout(() => {
                            setValues({ ...values, pos_graduation_student_value: dynamicCost(event.target.value) });
                          }, 100);
                        }}
                      />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 6 }}
                      error={errors.pos_graduation_student_associated_value}
                      field="pos_graduation_student_associated_value"
                      label={t('fields:pos_graduation_student_associated_value')}
                      required
                    >
                      <InputCurrency
                        name="pos_graduation_student_associated_value"
                        currency="R$"
                        number={false}
                        maxLength="9"
                        disabled={permissions && !permissions.includes('@paymentLot/edit')}
                        onChange={(event) => {
                          setTimeout(() => {
                            setValues({
                              ...values,
                              pos_graduation_student_associated_value: dynamicCost(event.target.value),
                            });
                          }, 100);
                        }}
                      />
                    </FormControl>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Modal>
      )}
    </Formik>
  );
}
